/**
 * Engineers Management Page
 * 
 * エンジニア情報の管理ページ
 * 主な機能:
 * - エンジニア一覧表示と検索
 * - 新規エンジニアの登録
 * - エンジニア情報の編集と削除
 * - スキルや経験によるフィルタリング
 * 
 * @component
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
  Checkbox,
  FormControlLabel,
  Divider,
  Autocomplete,
} from '@mui/material';
import {
  Search,
  Add,
  Edit,
  Delete,
  Person,
  Email,
  LocationOn,
  Code,
  Work,
  FilterList,
  AddCircle,
  RemoveCircle,
  Upload,
  GetApp,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { engineersService } from '../services/supabase';
import { EngineerWithSkills, CreateEngineerRequest } from '../types';
import ConfirmDialog from '../components/ConfirmDialog';
import { validateEmail, validatePhone, validateRequired, validateAndSanitizeInput } from '../utils/validation';
import { getStatusColor, getStatusText } from '../utils/helpers';
import { SkillSheetImportService, ImportResult, SkillSheetData } from '../services/skillSheetImportService';
import AIDataFormatter, { FormattingResult } from '../services/aiDataFormatter';
import { commonSkills, isValidSkill } from '../constants/skills';

const Engineers: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [openDialog, setOpenDialog] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  
  // Supabase state
  const [engineers, setEngineers] = useState<EngineerWithSkills[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  
  // Import state
  const [openImportDialog, setOpenImportDialog] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importPreviewData, setImportPreviewData] = useState<SkillSheetData[]>([]);
  const [importing, setImporting] = useState(false);
  const [useAIFormatting, setUseAIFormatting] = useState(true);
  const [calculateProjectExperience, setCalculateProjectExperience] = useState(true);
  const [formattingResults, setFormattingResults] = useState<FormattingResult[]>([]);
  
  // Form state
  const [formData, setFormData] = useState<CreateEngineerRequest>({
    name: '',
    email: '',
    phone: '',
    age: undefined,
    gender: undefined,
    nearest_station: '',
    address: '',
    education: '',
    location: '',
    hourly_rate: 0,
    availability_status: 'available',
    remote_work_preference: false,
    bio: '',
    skills: []
  });

  // Skills form state
  const [skillsForm, setSkillsForm] = useState<{
    skill_name: string;
    skill_level: 'beginner' | 'intermediate' | 'advanced' | 'expert';
    years_experience?: number;
  }[]>([]);

  // Load engineers data
  useEffect(() => {
    loadEngineers();
  }, []);

  const loadEngineers = async () => {
    setLoading(true);
    const result = await engineersService.getAll();
    if (result.error) {
      setError(result.error);
      setSnackbar({ open: true, message: `データの取得に失敗しました: ${result.error}`, severity: 'error' });
    } else {
      setEngineers(result.data || []);
    }
    setLoading(false);
  };

  // Form reset function
  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      age: undefined,
      gender: undefined,
      nearest_station: '',
      address: '',
      education: '',
      location: '',
      hourly_rate: 0,
      availability_status: 'available',
      remote_work_preference: false,
      bio: '',
      skills: []
    });
    setSkillsForm([]);
  };



  const handleCreateEngineer = async () => {
    try {
      // Validate required fields
      const nameValidation = validateRequired(formData.name, '名前');
      if (!nameValidation.isValid) {
        setSnackbar({ open: true, message: nameValidation.errors[0], severity: 'error' });
        return;
      }

      // Validate email if provided
      if (formData.email) {
        const emailValidation = validateEmail(formData.email);
        if (!emailValidation.isValid) {
          setSnackbar({ open: true, message: emailValidation.errors[0], severity: 'error' });
          return;
        }
      }

      // Validate phone if provided
      if (formData.phone) {
        const phoneValidation = validatePhone(formData.phone);
        if (!phoneValidation.isValid) {
          setSnackbar({ open: true, message: phoneValidation.errors[0], severity: 'error' });
          return;
        }
      }

      // Validate skills if provided
      const validSkills = skillsForm.filter(skill => skill.skill_name.trim() !== '');
      for (const skill of validSkills) {
        if (!skill.skill_name || skill.skill_name.trim().length === 0) {
          setSnackbar({ open: true, message: 'スキル名を入力してください', severity: 'error' });
          return;
        }
        if (!isValidSkill(skill.skill_name)) {
          setSnackbar({ open: true, message: 'プログラミング言語・フレームワークを選択してください', severity: 'error' });
          return;
        }
        if (skill.skill_name.trim().length > 100) {
          setSnackbar({ open: true, message: 'スキル名は100文字以下で入力してください', severity: 'error' });
          return;
        }
        if (!['beginner', 'intermediate', 'advanced', 'expert'].includes(skill.skill_level)) {
          setSnackbar({ open: true, message: '有効なスキルレベルを選択してください', severity: 'error' });
          return;
        }
        if (skill.years_experience !== undefined && (skill.years_experience < 0 || skill.years_experience > 50)) {
          setSnackbar({ open: true, message: '経験年数は0～50年の範囲で入力してください', severity: 'error' });
          return;
        }
      }

      // 長さ制限のチェック
      if (formData.name.length > 100) {
        setSnackbar({ open: true, message: '名前は100文字以下で入力してください', severity: 'error' });
        return;
      }
      if (formData.email && formData.email.length > 255) {
        setSnackbar({ open: true, message: 'メールアドレスは255文字以下で入力してください', severity: 'error' });
        return;
      }
      if (formData.phone && formData.phone.length > 20) {
        setSnackbar({ open: true, message: '電話番号は20文字以下で入力してください', severity: 'error' });
        return;
      }
      if (formData.location && formData.location.length > 255) {
        setSnackbar({ open: true, message: '住所は255文字以下で入力してください', severity: 'error' });
        return;
      }
      if (formData.bio && formData.bio.length > 1000) {
        setSnackbar({ open: true, message: '自己紹介は1000文字以下で入力してください', severity: 'error' });
        return;
      }

      // スキル名の長さチェック
      for (const skill of validSkills) {
        if (skill.skill_name.length > 100) {
          setSnackbar({ open: true, message: 'スキル名は100文字以下で入力してください', severity: 'error' });
          return;
        }
      }

      // Sanitize inputs and skills
      const sanitizedSkills = validSkills.map(skill => ({
        skill_name: validateAndSanitizeInput(skill.skill_name.trim(), 100),
        skill_level: skill.skill_level as 'beginner' | 'intermediate' | 'advanced' | 'expert',
        years_experience: skill.years_experience ? Math.round(skill.years_experience) : 0
      }));

      const sanitizedData = {
        ...formData,
        name: validateAndSanitizeInput(formData.name, 100),
        email: formData.email ? validateAndSanitizeInput(formData.email, 255) : '',
        phone: formData.phone ? validateAndSanitizeInput(formData.phone, 20) : '',
        location: formData.location ? validateAndSanitizeInput(formData.location, 255) : '',
        bio: formData.bio ? validateAndSanitizeInput(formData.bio, 1000) : '',
        skills: sanitizedSkills
      };
      

      const result = await engineersService.create(sanitizedData);
      if (result.error) {
        let errorMessage = 'エンジニアの登録に失敗しました';
        
        // メールアドレス重複エラーの場合、わかりやすいメッセージに変更
        if (result.error.includes('duplicate key value violates unique constraint "engineers_email_key"')) {
          errorMessage = 'このメールアドレスは既に登録されています。別のメールアドレスを使用してください。';
        } else if (result.error.includes('duplicate') && result.error.includes('email')) {
          errorMessage = 'このメールアドレスは既に登録されています。別のメールアドレスを使用してください。';
        } else {
          errorMessage = `エンジニアの登録に失敗しました: ${result.error}`;
        }
        
        setSnackbar({ open: true, message: errorMessage, severity: 'error' });
      } else {
        setSnackbar({ open: true, message: 'エンジニアを登録しました', severity: 'success' });
        setOpenDialog(false);
        resetForm();
        loadEngineers(); // Refresh data
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'エンジニアの登録中にエラーが発生しました', severity: 'error' });
    }
  };

  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; engineer: { id: number; name: string } | null }>({
    open: false,
    engineer: null
  });

  const handleDeleteEngineer = (id: number, name: string) => {
    setDeleteConfirm({ open: true, engineer: { id, name } });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.engineer) return;

    const result = await engineersService.delete(deleteConfirm.engineer.id);
    if (result.error) {
      setSnackbar({ open: true, message: '削除に失敗しました', severity: 'error' });
    } else {
      setSnackbar({ open: true, message: 'エンジニアを削除しました', severity: 'success' });
      loadEngineers(); // Refresh data
    }
    setDeleteConfirm({ open: false, engineer: null });
  };

  // Import functions
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setImportFile(file);
      setImportResult(null);
      setImportPreviewData([]);
    }
  };

  const handleImportPreview = async () => {
    if (!importFile) {
      setSnackbar({ open: true, message: 'ファイルを選択してください', severity: 'error' });
      return;
    }

    setImporting(true);
    try {
      
      // 単一エンジニアモードで解析
      const result = await SkillSheetImportService.importFile(importFile, {
        skipEmptyRows: true,
        maxRows: 1, // 単一エンジニアなので1件に制限
        strictValidation: false,
        useAI: true, // AI機能を有効化
        fallbackToAI: true, // 従来方式失敗時のAIフォールバック
        singleEngineerMode: true, // 単一エンジニアモード
        calculateExperienceFromProjects: calculateProjectExperience // 案件から経験年数を計算
      });

      setImportResult(result);
      if (result.success && result.data) {
        let processedData = result.data;
        
        // AI整形機能を使用する場合
        if (useAIFormatting && result.data.length > 0) {
          try {
            const formattingResults = await AIDataFormatter.formatBatchData(result.data);
            setFormattingResults(formattingResults);
            
            // 整形に成功したデータを使用
            processedData = formattingResults
              .filter(fr => fr.success && fr.data)
              .map(fr => fr.data!);
            
          } catch (aiError) {
            // AI整形に失敗、元データを使用
          }
        }
        
        setImportPreviewData(processedData);
        
        let message = `エンジニア情報を読み込みました`;
        if (result.aiAnalysis) {
          message += ` (AI解析使用`;
          if (result.confidence) {
            message += `, 信頼度: ${Math.round(result.confidence * 100)}%`;
          }
          message += `)`;
        }
        if (useAIFormatting && formattingResults.length > 0) {
          const totalChanges = formattingResults.reduce((sum, fr) => sum + fr.changes.length, 0);
          if (totalChanges > 0) {
            message += ` + AI整形済み(${totalChanges}件の改善)`;
          }
        }
        
        setSnackbar({ 
          open: true, 
          message, 
          severity: 'success' 
        });
      } else {
        // エラーメッセージを改善
        let errorMessage = 'データの読み込みに失敗しました';
        
        if (result.errors.length > 0) {
          // 最も重要なエラーメッセージを選択
          const criticalErrors = result.errors.filter(e => 
            e.includes('ヘッダー') || e.includes('フォーマット') || e.includes('形式')
          );
          
          if (criticalErrors.length > 0) {
            errorMessage = criticalErrors[0];
          } else {
            errorMessage = result.errors[0];
          }
        }
        
        if (result.suggestions && result.suggestions.length > 0) {
          errorMessage += `\n提案: ${result.suggestions[0]}`;
        }
        
        setSnackbar({ 
          open: true, 
          message: errorMessage, 
          severity: 'error' 
        });
      }
    } catch (error) {
      const errorMessage = `インポート処理中にエラーが発生しました: ${(error as Error).message}`;
      setSnackbar({ 
        open: true, 
        message: errorMessage, 
        severity: 'error' 
      });
    } finally {
      setImporting(false);
    }
  };

  const handleImportSave = async () => {
    if (!importPreviewData || importPreviewData.length === 0) {
      setSnackbar({ open: true, message: '保存するデータがありません', severity: 'error' });
      return;
    }

    setImporting(true);
    let successCount = 0;
    let errorCount = 0;
    const errors: string[] = [];

    try {
      for (let index = 0; index < importPreviewData.length; index++) {
        const skillSheetData = importPreviewData[index];
        try {
          const engineerRequest = SkillSheetImportService.convertToEngineerRequest(skillSheetData);
          

          const result = await engineersService.create(engineerRequest);
          
          if (result.error) {
            errorCount++;
            
            // エラーメッセージをわかりやすく変換
            let friendlyError = result.error;
            if (result.error.includes('value too long for type character varying')) {
              if (result.error.includes('email')) {
                friendlyError = 'メールアドレスが長すぎます（255文字以下にしてください）';
              } else if (result.error.includes('location')) {
                friendlyError = '住所が長すぎます（255文字以下にしてください）';
              } else if (result.error.includes('phone')) {
                friendlyError = '電話番号が長すぎます（20文字以下にしてください）';
              } else {
                friendlyError = '入力データが長すぎます。データを短くしてください。';
              }
            } else if (result.error.includes('duplicate key value violates unique constraint')) {
              if (result.error.includes('email')) {
                friendlyError = 'このメールアドレスは既に登録されています。';
              } else {
                friendlyError = '重複するデータが存在します。';
              }
            }
            
            errors.push(`${index + 1}行目（${skillSheetData.name || '名前不明'}）: ${friendlyError}`);
          } else {
            successCount++;
            
            // 技術経験データの保存（案件から計算されている場合）
            if (result.data && calculateProjectExperience && importResult?.debugInfo?.experienceCalculation) {
              try {
                const engineerId = result.data.id;
                const experienceData = importResult.debugInfo.experienceCalculation;
                
                // 技術経験データを保存
                if (Object.keys(experienceData.technologies).length > 0) {
                  const techResult = await engineersService.saveTechnologyExperience(engineerId, experienceData.technologies);
                  if (techResult.error) {
                    console.warn('技術経験データの保存に失敗:', techResult.error);
                  }
                }
                
                // インポート履歴を保存
                const historyResult = await engineersService.saveImportHistory(engineerId, {
                  fileName: importFile?.name || 'unknown',
                  fileType: importFile?.name?.split('.').pop() || 'unknown',
                  technologiesFound: Object.keys(experienceData.technologies),
                  projectsFound: Object.values(experienceData.technologies).reduce((sum: number, tech: any) => sum + tech.projects.length, 0),
                  confidence: importResult.confidence,
                  insights: importResult.insights,
                  warnings: importResult.warnings,
                  rawData: { experienceCalculation: experienceData }
                });
                if (historyResult.error) {
                  console.warn('インポート履歴の保存に失敗:', historyResult.error);
                }
              } catch (techError) {
                console.warn('技術経験データ処理中にエラー:', techError);
              }
            }
          }
        } catch (error) {
          errorCount++;
          const errorMessage = (error as Error).message;
          errors.push(`${index + 1}行目（${skillSheetData.name || '名前不明'}）: ${errorMessage}`);
        }
      }

      // インポート結果の処理
      if (successCount > 0) {
        // データを確実に更新してからダイアログを閉じる - 短い遅延を追加
        await loadEngineers();
        
        // 100ms待機してUIの更新を確実にする
        await new Promise(resolve => setTimeout(resolve, 100));
        
        setSnackbar({ 
          open: true, 
          message: `エンジニアを登録しました${errorCount > 0 ? '（一部エラーが発生）' : ''}`, 
          severity: (successCount === importPreviewData.length ? 'success' : 'error') as 'success' | 'error'
        });
        
        setOpenImportDialog(false);
        setImportFile(null);
        setImportResult(null);
        setImportPreviewData([]);
      }

      if (errorCount > 0 && successCount === 0) {
        setSnackbar({ 
          open: true, 
          message: `登録に失敗しました: ${errors[0]}`, 
          severity: 'error'
        });
        
        // すべて失敗した場合のみダイアログを閉じる
        if (errorCount === importPreviewData.length) {
          setOpenImportDialog(false);
          setImportFile(null);
          setImportResult(null);
          setImportPreviewData([]);
          setFormattingResults([]);
        }
      }
    } catch (error) {
      setSnackbar({ 
        open: true, 
        message: 'インポート処理中にエラーが発生しました', 
        severity: 'error' 
      });
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = () => {
    const templateData = [
      ['名前', 'メールアドレス', '電話番号', '年齢', '性別', '最寄駅', '住所', '最終学歴', '現在の住所', '経験年数', '時給', '稼働状況', 'リモートワーク希望', '自己紹介・経歴', 'スキル1', 'スキル1レベル', 'スキル1経験年数', 'スキル2', 'スキル2レベル', 'スキル2経験年数']
    ];
    
    const csvContent = templateData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'engineer_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };


  // 単一の .filter() に統合（js-combine-iterations ルール適用）
  const filteredEngineers = engineers.filter(engineer => {
    if (filterStatus !== 'all' && engineer.availability_status !== filterStatus) return false;
    const searchLower = searchTerm.toLowerCase();
    return engineer.name.toLowerCase().includes(searchLower) ||
           (engineer.email && engineer.email.toLowerCase().includes(searchLower)) ||
           engineer.skills.some(skill => skill.skill_name.toLowerCase().includes(searchLower));
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Skills management functions
  const addSkill = () => {
    setSkillsForm(prev => [...prev, {
      skill_name: '',
      skill_level: 'beginner',
      years_experience: 0
    }]);
  };

  const removeSkill = (index: number) => {
    setSkillsForm(prev => prev.filter((_, i) => i !== index));
  };

  const updateSkill = (index: number, field: string, value: any) => {
    setSkillsForm(prev => prev.map((skill, i) => 
      i === index ? { ...skill, [field]: value } : skill
    ));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          エンジニア管理
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<GetApp />}
            onClick={handleDownloadTemplate}
            size="small"
          >
            テンプレート
          </Button>
          <Button
            variant="outlined"
            startIcon={<Upload />}
            onClick={() => setOpenImportDialog(true)}
            color="secondary"
          >
            インポート
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              resetForm();
              setOpenDialog(true);
            }}
            sx={{ bgcolor: '#3498db' }}
          >
            新規登録
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* 検索・フィルター */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2 }}>
            <TextField
              fullWidth
              placeholder="エンジニア名・スキルで検索"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl fullWidth>
              <InputLabel>ステータス</InputLabel>
              <Select
                value={filterStatus}
                label="ステータス"
                onChange={(e) => setFilterStatus(e.target.value)}
                startAdornment={<FilterList sx={{ mr: 1 }} />}
              >
                <MenuItem value="all">すべて</MenuItem>
                <MenuItem value="available">稼働可能</MenuItem>
                <MenuItem value="busy">稼働中</MenuItem>
                <MenuItem value="unavailable">稼働不可</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* エンジニア一覧 */}
      <Box sx={{ display: 'grid', gap: 2 }}>
        {filteredEngineers
          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          .map((engineer) => (
          <Card 
            key={engineer.id} 
            sx={{ 
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': { 
                transform: 'translateY(-2px)', 
                boxShadow: 3 
              }
            }}
            onClick={() => navigate(`/engineer/${engineer.id}`)}
          >
            <CardContent>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '300px 1fr auto' }, gap: 3, alignItems: 'start' }}>
                {/* 基本情報 */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar 
                    sx={{ width: 56, height: 56, bgcolor: '#3498db' }}
                  >
                    <Person />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {engineer.name}
                    </Typography>
                    {engineer.email && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <Email fontSize="small" color="action" />
                        <Typography variant="body2" color="textSecondary">
                          {engineer.email}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                      {engineer.age != null && engineer.gender && (
                        <Typography variant="body2" color="textSecondary">
                          年齢: {engineer.age}歳 / 性別: {
                            engineer.gender === 'male' ? '男性' : 
                            engineer.gender === 'female' ? '女性' : 
                            engineer.gender === 'other' ? 'その他' : '回答しない'
                          }
                        </Typography>
                      )}
                      {engineer.nearest_station && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LocationOn fontSize="small" color="action" />
                          <Typography variant="body2" color="textSecondary">
                            最寄駅: {engineer.nearest_station}
                          </Typography>
                        </Box>
                      )}
                      {engineer.address && (
                        <Typography variant="body2" color="textSecondary">
                          住所: {engineer.address}
                        </Typography>
                      )}
                      {engineer.education && (
                        <Typography variant="body2" color="textSecondary">
                          最終学歴: {engineer.education}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  {engineer.bio && (
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1, fontSize: '0.8rem' }}>
                      {engineer.bio.length > 50 ? `${engineer.bio.substring(0, 50)}...` : engineer.bio}
                    </Typography>
                  )}
                </Box>

                {/* スキル・経験 */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                    <Code fontSize="small" sx={{ mr: 0.5 }} />
                    業務内容・使用言語等
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                    {engineer.skills.slice(0, 4).map((skill) => (
                      <Chip
                        key={skill.id}
                        label={`${skill.skill_name} ${skill.years_experience || 0}年`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                    {engineer.skills.length > 4 && (
                      <Chip
                        label={`+${engineer.skills.length - 4}`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {engineer.experience_years != null && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Work fontSize="small" color="action" />
                        <Typography variant="body2" color="textSecondary">
                          {engineer.experience_years}年の経験
                        </Typography>
                      </Box>
                    )}
                    {engineer.hourly_rate != null && (
                      <Typography variant="body2" color="textSecondary">
                        ¥{engineer.hourly_rate.toLocaleString()}/時間
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* ステータス・アクション */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                  <Chip
                    label={getStatusText(engineer.availability_status)}
                    color={getStatusColor(engineer.availability_status) as any}
                    sx={{ fontWeight: 'bold' }}
                  />
                  <Typography variant="caption" color="textSecondary">
                    登録日: {new Date(engineer.created_at).toLocaleDateString()}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      startIcon={<Edit />}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/engineer/${engineer.id}`);
                      }}
                    >
                      編集
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<Delete />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteEngineer(engineer.id, engineer.name);
                      }}
                    >
                      削除
                    </Button>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* ページネーション */}
      <Card sx={{ mt: 3 }}>
        <TablePagination
          component="div"
          count={filteredEngineers.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="表示件数:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} / ${count !== -1 ? count : `more than ${to}`}`
          }
        />
      </Card>

      {/* 新規登録ダイアログ */}
      <Dialog open={openDialog} onClose={() => {
        setOpenDialog(false);
        resetForm();
      }} maxWidth="md" fullWidth>
        <DialogTitle>新しいエンジニアを登録</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
            <TextField 
              fullWidth 
              label="名前" 
              required 
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              inputProps={{ maxLength: 100 }}
              helperText={`${formData.name.length}/100文字`}
            />
            <TextField 
              fullWidth 
              label="メールアドレス" 
              type="email" 
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              inputProps={{ maxLength: 255 }}
              helperText={formData.email ? `${formData.email.length}/255文字` : ''}
            />
            <TextField 
              fullWidth 
              label="電話番号" 
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              inputProps={{ maxLength: 20 }}
              helperText={formData.phone ? `${formData.phone.length}/20文字` : ''}
            />
            <TextField 
              fullWidth 
              label="年齢" 
              type="number" 
              value={formData.age || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, age: parseInt(e.target.value) || undefined }))}
              inputProps={{ min: 18, max: 100 }}
            />
            <FormControl fullWidth>
              <InputLabel>性別</InputLabel>
              <Select
                value={formData.gender || ''}
                label="性別"
                onChange={(e) => setFormData(prev => ({ ...prev, gender: e.target.value as any }))}
              >
                <MenuItem value="male">男性</MenuItem>
                <MenuItem value="female">女性</MenuItem>
                <MenuItem value="other">その他</MenuItem>
                <MenuItem value="prefer_not_to_say">回答しない</MenuItem>
              </Select>
            </FormControl>
            <TextField 
              fullWidth 
              label="最寄駅" 
              value={formData.nearest_station}
              onChange={(e) => setFormData(prev => ({ ...prev, nearest_station: e.target.value }))}
              inputProps={{ maxLength: 100 }}
              helperText={formData.nearest_station ? `${formData.nearest_station.length}/100文字` : ''}
            />
            <TextField 
              fullWidth 
              label="住所" 
              value={formData.address}
              onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
              inputProps={{ maxLength: 255 }}
              helperText={formData.address ? `${formData.address.length}/255文字` : ''}
            />
            <TextField 
              fullWidth 
              label="最終学歴" 
              value={formData.education}
              onChange={(e) => setFormData(prev => ({ ...prev, education: e.target.value }))}
              inputProps={{ maxLength: 255 }}
              helperText={formData.education ? `${formData.education.length}/255文字` : ''}
            />
            <TextField 
              fullWidth 
              label="住所（現在の住所以外）" 
              value={formData.location}
              onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
              inputProps={{ maxLength: 255 }}
              helperText={formData.location ? `${formData.location.length}/255文字` : ''}
            />
            <TextField 
              fullWidth 
              label="経験年数" 
              type="number" 
              value={formData.experience_years || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, experience_years: parseInt(e.target.value) || undefined }))}
            />
            <TextField 
              fullWidth 
              label="時給（円）" 
              type="number" 
              value={formData.hourly_rate || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, hourly_rate: parseInt(e.target.value) || undefined }))}
            />
            <FormControl fullWidth>
              <InputLabel>稼働状況</InputLabel>
              <Select
                value={formData.availability_status}
                label="稼働状況"
                onChange={(e) => setFormData(prev => ({ ...prev, availability_status: e.target.value as any }))}
              >
                <MenuItem value="available">稼働可能</MenuItem>
                <MenuItem value="busy">稼働中</MenuItem>
                <MenuItem value="unavailable">稼働不可</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.remote_work_preference || false}
                  onChange={(e) => setFormData(prev => ({ ...prev, remote_work_preference: e.target.checked }))}
                />
              }
              label="リモートワーク希望"
            />
            <TextField 
              fullWidth 
              label="自己紹介・経歴" 
              multiline
              rows={3}
              value={formData.bio}
              onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
              inputProps={{ maxLength: 1000 }}
              helperText={formData.bio ? `${formData.bio.length}/1000文字` : ''}
            />
            
            <Divider sx={{ my: 2 }} />
            
            {/* 業務内容・使用言語等セクション */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">業務内容・使用言語等</Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddCircle />}
                  onClick={addSkill}
                  size="small"
                >
                  業務・言語追加
                </Button>
              </Box>
              
              {skillsForm.map((skill, index) => (
                <Box key={`skill-${index}-${skill.skill_name}`} sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: 1, mb: 2, alignItems: 'center' }}>
                  <Autocomplete
                    options={commonSkills}
                    value={skill.skill_name}
                    onChange={(event, newValue) => {
                      updateSkill(index, 'skill_name', newValue || '');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="業務内容・使用言語"
                        placeholder="プログラミング言語・フレームワークを選択してください"
                        size="small"
                        error={skill.skill_name !== '' && !isValidSkill(skill.skill_name)}
                        helperText={skill.skill_name !== '' && !isValidSkill(skill.skill_name) ? 'プログラミング言語・フレームワークを選択してください' : ''}
                      />
                    )}
                  />
                  <FormControl size="small">
                    <InputLabel>レベル</InputLabel>
                    <Select
                      value={skill.skill_level}
                      label="レベル"
                      onChange={(e) => updateSkill(index, 'skill_level', e.target.value)}
                    >
                      <MenuItem value="beginner">初級</MenuItem>
                      <MenuItem value="intermediate">中級</MenuItem>
                      <MenuItem value="advanced">上級</MenuItem>
                      <MenuItem value="expert">エキスパート</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="経験年数"
                    type="number"
                    value={skill.years_experience || ''}
                    onChange={(e) => updateSkill(index, 'years_experience', parseInt(e.target.value) || 0)}
                    size="small"
                    inputProps={{ min: 0, max: 50 }}
                  />
                  <IconButton
                    color="error"
                    onClick={() => removeSkill(index)}
                    size="small"
                  >
                    <RemoveCircle />
                  </IconButton>
                </Box>
              ))}
              
              {skillsForm.length === 0 && (
                <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                  業務内容・使用言語等が登録されていません。「業務・言語追加」ボタンで追加してください。
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenDialog(false);
            resetForm();
          }}>キャンセル</Button>
          <Button variant="contained" onClick={handleCreateEngineer}>
            登録
          </Button>
        </DialogActions>
      </Dialog>

      {/* スナックバー */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* 削除確認ダイアログ */}
      <ConfirmDialog
        open={deleteConfirm.open}
        title="エンジニア削除"
        message={`${deleteConfirm.engineer?.name || ''}を削除しますか？この操作は取り消せません。`}
        type="warning"
        confirmText="削除"
        cancelText="キャンセル"
        confirmColor="error"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm({ open: false, engineer: null })}
      />

      {/* インポートダイアログ */}
      <Dialog 
        open={openImportDialog} 
        onClose={() => {
          setOpenImportDialog(false);
          setImportFile(null);
          setImportResult(null);
          setImportPreviewData([]);
          setFormattingResults([]);
          setCalculateProjectExperience(true);
        }} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle>
          スキルシートインポート
          <Typography variant="body2" color="textSecondary">
            1つのExcel(.xlsx/.xls)またはCSV(.csv)ファイルから1人のエンジニア情報を登録できます
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gap: 3, pt: 2 }}>
            {/* ファイル選択 */}
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                1. ファイルを選択
              </Typography>
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileSelect}
                style={{ width: '100%', padding: '8px' }}
              />
              {importFile && (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  選択済み: {importFile.name}
                </Typography>
              )}
            </Box>

            {/* AI整形オプション */}
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={useAIFormatting}
                    onChange={(e) => setUseAIFormatting(e.target.checked)}
                  />
                }
                label="AI整形機能を使用（名前・スキル・メールアドレス等の自動修正）"
              />
            </Box>

            {/* 案件経験年数計算オプション */}
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={calculateProjectExperience}
                    onChange={(e) => setCalculateProjectExperience(e.target.checked)}
                  />
                }
                label="案件情報から技術経験年数を自動計算（推奨）"
              />
              <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5, ml: 4 }}>
                スキルシート内の案件履歴を解析して、各技術の実際の経験年数を算出します
              </Typography>
            </Box>

            {/* プレビューボタン */}
            <Box>
              <Button
                variant="contained"
                onClick={handleImportPreview}
                disabled={!importFile || importing}
                startIcon={importing ? <CircularProgress size={16} /> : <Search />}
              >
                {importing ? 'プレビュー中...' : '2. データをプレビュー'}
              </Button>
            </Box>

            {/* インポート結果表示 */}
            {importResult && (
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                  インポート結果
                  {importResult.aiAnalysis && (
                    <Chip 
                      label={`AI解析 ${importResult.confidence ? `(${Math.round(importResult.confidence * 100)}%)` : ''}`}
                      size="small" 
                      color="primary" 
                      sx={{ ml: 1 }} 
                    />
                  )}
                </Typography>
                
                {/* AI分析情報表示 */}
                {importResult.insights && importResult.insights.length > 0 && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">AI解析情報:</Typography>
                    {importResult.insights.map((insight, index) => (
                      <Typography key={index} variant="body2">• {insight}</Typography>
                    ))}
                  </Alert>
                )}
                
                {/* エラー表示 */}
                {importResult.errors.length > 0 && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">エラー:</Typography>
                    {importResult.errors.slice(0, 3).map((error, index) => (
                      <Typography key={index} variant="body2">• {error}</Typography>
                    ))}
                    {importResult.errors.length > 3 && (
                      <Typography variant="body2" color="textSecondary">
                        ...他 {importResult.errors.length - 3} 件のエラー
                      </Typography>
                    )}
                  </Alert>
                )}
                
                {/* 警告表示 */}
                {importResult.warnings.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">警告:</Typography>
                    {importResult.warnings.map((warning, index) => (
                      <Typography key={index} variant="body2">• {warning}</Typography>
                    ))}
                  </Alert>
                )}
                
                {/* AI提案表示 */}
                {importResult.suggestions && importResult.suggestions.length > 0 && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">提案:</Typography>
                    {importResult.suggestions.map((suggestion, index) => (
                      <Typography key={index} variant="body2">• {suggestion}</Typography>
                    ))}
                  </Alert>
                )}

                {/* AI整形結果表示 */}
                {useAIFormatting && formattingResults.length > 0 && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">AI整形結果:</Typography>
                    {formattingResults.map((result, index) => (
                      <Box key={index} sx={{ mt: 1 }}>
                        {result.success && (
                          <Box>
                            {result.improvements.length > 0 && (
                              <Typography variant="body2" sx={{ color: 'success.main' }}>
                                ✓ {result.improvements.join(', ')}
                              </Typography>
                            )}
                            {result.changes.length > 0 && (
                              <Typography variant="body2" sx={{ fontSize: '0.8rem', ml: 1 }}>
                                変更箇所: {result.changes.map(c => `${c.field}: ${c.reason}`).join(', ')}
                              </Typography>
                            )}
                          </Box>
                        )}
                        {result.warnings.length > 0 && (
                          <Typography variant="body2" sx={{ color: 'warning.main' }}>
                            ⚠ {result.warnings.join(', ')}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Alert>
                )}

                {/* デバッグ情報表示 */}
                {importResult.debugInfo && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">詳細解析情報:</Typography>
                    {importResult.debugInfo.extractionSteps && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>抽出プロセス:</Typography>
                        {importResult.debugInfo.extractionSteps.slice(0, 5).map((step: any, index: number) => (
                          <Typography key={index} variant="body2" sx={{ fontSize: '0.8rem', ml: 1 }}>
                            {step.step}. {step.description}: {typeof step.result === 'string' ? step.result : JSON.stringify(step.result)} 
                            (信頼度: {Math.round((step.confidence || 0) * 100)}%)
                          </Typography>
                        ))}
                      </Box>
                    )}
                    {importResult.debugInfo.cellAnalysis && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          高信頼度抽出セル:
                        </Typography>
                        {importResult.debugInfo.cellAnalysis
                          .filter((cell: any) => cell.confidence > 0.7)
                          .slice(0, 3)
                          .map((cell: any, index: number) => (
                            <Typography key={index} variant="body2" sx={{ fontSize: '0.8rem', ml: 1 }}>
                              "{cell.cellValue}" → {cell.detectedType} (信頼度: {Math.round(cell.confidence * 100)}%)
                            </Typography>
                          ))}
                      </Box>
                    )}
                  </Alert>
                )}

                {/* プレビューデータ表示 */}
                {importPreviewData.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      プレビュー:
                    </Typography>
                    <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                      {importPreviewData.slice(0, 1).map((data, index) => (
                        <Card key={index} variant="outlined" sx={{ mb: 1, p: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                              {data.name || '名前未設定'}
                            </Typography>
                            <Chip 
                              label={data.availability_status || 'available'} 
                              size="small" 
                              color={data.availability_status === 'available' ? 'success' : 
                                     data.availability_status === 'busy' ? 'warning' : 'error'}
                            />
                          </Box>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                            📧 {data.email || 'メール未設定'} | 
                            💼 経験: {data.experience_years || 0}年 | 
                            💰 時給: {data.hourly_rate ? `¥${data.hourly_rate.toLocaleString()}` : '未設定'}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                            🛠️ スキル ({data.skills.length}件): {
                              data.skills.length > 0 
                                ? data.skills.slice(0, 3).map(s => {
                                    const levelText = s.skill_level === 'beginner' ? '初級' :
                                                     s.skill_level === 'intermediate' ? '中級' :
                                                     s.skill_level === 'advanced' ? '上級' : 'エキスパート';
                                    const yearsText = s.years_experience ? `・${s.years_experience}年` : '';
                                    return `${s.skill_name}(${levelText}${yearsText})`;
                                  }).join(', ') + (data.skills.length > 3 ? ` 他${data.skills.length - 3}件` : '')
                                : 'スキル情報なし'
                            }
                          </Typography>
                          {data.location && (
                            <Typography variant="body2" color="textSecondary">
                              📍 {data.location}
                            </Typography>
                          )}
                          {data.bio && (
                            <Typography variant="body2" color="textSecondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                              💬 {data.bio.length > 80 ? `${data.bio.substring(0, 80)}...` : data.bio}
                            </Typography>
                          )}
                          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                            🔍 リモート希望: {data.remote_work_preference ? 'あり' : 'なし'} | 
                            稼働状況: {data.availability_status === 'available' ? '稼働可能' : 
                                     data.availability_status === 'busy' ? '稼働中' : '稼働不可'}
                          </Typography>
                        </Card>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenImportDialog(false);
            setImportFile(null);
            setImportResult(null);
            setImportPreviewData([]);
            setFormattingResults([]);
            setCalculateProjectExperience(true);
          }}>
            キャンセル
          </Button>
          <Button
            variant="contained"
            onClick={handleImportSave}
            disabled={!importPreviewData || importPreviewData.length === 0 || importing}
            startIcon={importing ? <CircularProgress size={16} /> : <Upload />}
          >
            {importing ? '保存中...' : '3. データを保存'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default Engineers;